import React from 'react';
import { AuthStatus } from '@/enums';
import { createStackNavigator } from '@react-navigation/stack';
import { LogoLoadingIndicator } from '@/components/logo-loading-indicator';
import { NewOrderActionsContextProvider } from '@/contexts/new-order-actions-context';
import { NewOrderContextProvider } from '@/contexts/new-order-context';
import { NewOrderQuantityStateContextProvider } from '@/contexts/new-order-quantity-state-context';
import { NotAuthorizedScreen } from '@/screens/not-authorized-screen';
import { OrderGuideSettingsContextProvider } from '@/contexts/order-guide-settings-context';
import { OrderGuideStoreProvider } from '@/stores/order-guide-store';
import { PaymentsContextProvider } from '@/contexts/payments-context';
import { SMART_ORDER_CONTAINER_ROUTES } from './routes';
import { SmartOrderContainerStackParams } from './types';
import { SmartOrderSubmittedScreen } from '@/screens/smart-order/smart-order-submitted-screen';
import { useDefaultStackScreenOptions } from '@/hooks/use-default-stack-screen-options';
import { useMaybeUserContext } from '@/contexts/user-context';
import { VariantPackInfoContextProvider } from '@/contexts/variant-pack-info-context';

const Stack = createStackNavigator<SmartOrderContainerStackParams>();

const SmartOrderAppStack = () => {
  const screenOptions = useDefaultStackScreenOptions();

  return (
    <OrderGuideStoreProvider>
      <OrderGuideSettingsContextProvider>
        <NewOrderQuantityStateContextProvider>
          <NewOrderContextProvider>
            <NewOrderActionsContextProvider>
              <VariantPackInfoContextProvider>
                <PaymentsContextProvider>
                  <Stack.Navigator
                    initialRouteName={SMART_ORDER_CONTAINER_ROUTES.PlacedOrder}
                    screenOptions={screenOptions}
                  >
                    <Stack.Screen
                      component={SmartOrderSubmittedScreen}
                      name={SMART_ORDER_CONTAINER_ROUTES.PlacedOrder}
                    />
                  </Stack.Navigator>
                </PaymentsContextProvider>
              </VariantPackInfoContextProvider>
            </NewOrderActionsContextProvider>
          </NewOrderContextProvider>
        </NewOrderQuantityStateContextProvider>
      </OrderGuideSettingsContextProvider>
    </OrderGuideStoreProvider>
  );
};

export const SmartOrderContainerStack: React.FC = () => {
  const userContext = useMaybeUserContext();

  let BaseComponent: React.ComponentType<{}> | null = null;
  switch (userContext?.authStatus) {
    case AuthStatus.Authenticated:
      BaseComponent = SmartOrderAppStack;
      break;
    case AuthStatus.NotAuthorized:
      BaseComponent = NotAuthorizedScreen;
      break;
  }

  if (!BaseComponent) {
    return <LogoLoadingIndicator />;
  }

  return <BaseComponent />;
};
